/**
 * Wire protocol for the realtime transcription WebSocket (`/ws/transcription`).
 *
 * The browser never talks to Mistral directly — the API proxies the session so
 * the Mistral key stays server-side. Audio travels as raw binary frames
 * (16-bit little-endian PCM, mono); every other exchange is a JSON envelope
 * described below.
 */

/** PCM sample rates accepted by the realtime model. */
export const TRANSCRIPTION_SAMPLE_RATE = 16000;

/** Hard cap on a single dictation, enforced on both ends. */
export const TRANSCRIPTION_MAX_DURATION_MS = 5 * 60 * 1000;

/**
 * Longest silence tolerated before the server closes an idle session. Protects
 * against browsers that vanish without a close frame (mobile tab eviction).
 */
export const TRANSCRIPTION_IDLE_TIMEOUT_MS = 30 * 1000;

/** Window the client has to authenticate after opening the socket. */
export const TRANSCRIPTION_AUTH_TIMEOUT_MS = 10 * 1000;

/**
 * Streaming delay handed to the model, in milliseconds.
 *
 * The provider accepts 80-1200 or exactly 2400; anything else is rejected and
 * the session never opens. Measured on French dictation, repeated three times
 * per value and fully deterministic: 80ms gives 9.7% WER with first text after
 * ~1.5s, while 200ms, 1200ms and 2400ms all sit at 12.9% and only get slower.
 * More buffering does not buy accuracy here, so the floor wins on both axes.
 */
export const TRANSCRIPTION_STREAMING_DELAY_MS = 80;

/** Client → server messages. */
export type TranscriptionClientMessage =
  /**
   * Must be the first frame on the socket. The token is sent in the body rather
   * than the query string so it never lands in proxy or access logs.
   *
   * No language or vocabulary hint is carried here: the realtime model takes
   * neither and detects the spoken language on its own. Both are available on
   * the batch fallback, which does support them.
   */
  | {
      type: "auth";
      token: string;
      /** Attributes the dictation's cost to the caller's team in analytics. */
      teamId?: string;
    }
  /** Stop sending audio and flush the pending transcript. */
  | { type: "stop" }
  /** Abandon the session; no transcript is emitted. */
  | { type: "cancel" };

/** Server → client messages. */
export type TranscriptionServerMessage =
  /** Authentication accepted and the upstream session is open — start sending audio. */
  | { type: "ready"; sampleRate: number }
  /**
   * The full transcript so far, already corrected for product vocabulary.
   *
   * Sent whole rather than as an increment: vocabulary correction can rewrite
   * a word the client received earlier ("Sharp" once "Shaarp" is recognised),
   * which an append-only stream could never undo. The client replaces its text
   * with this value.
   */
  | { type: "transcript"; text: string }
  /** Detected spoken language, emitted at most once per session. */
  | { type: "language"; language: string }
  /** Final transcript. The socket closes right after. */
  | { type: "done"; text: string }
  /** Terminal failure. `retriable` tells the client whether to fall back to batch upload. */
  | { type: "error"; code: TranscriptionErrorCode; retriable: boolean };

export type TranscriptionErrorCode =
  | "unauthorized"
  | "unavailable"
  | "upstream_failed"
  | "too_long"
  | "rate_limited"
  | "internal";

/** Response of the batch transcription fallback endpoint. */
export type TranscriptionResult = {
  text: string;
};
